import React, { useCallback } from "react";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import useLocale from "@RHCommerceDev/hooks/useLocale";
import parsedCatalogNavigation from "@RHCommerceDev/utils/parseCatalogNavigationData";
import memoize from "@RHCommerceDev/utils/memoize";
import CatalogNavigation from "@RHCommerceDev/container-rhr-header/CatalogNavigation/CatalogNavigation";
import buildPath from "@RHCommerceDev/utils/buildPath";

const CatalogNavigationContainer = () => {
  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const locale = useLocalization();
  const splitLocale = useLocale();

  const {
    state: headerState,
    closeCategoryDropdown,
    openCategoryDropdown
  } = useHeaderAtom();

  // Parse navigation into structured categories
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    false
  );
  const categories = navigationData?.childCategories ?? [];

  const isFrenchCanada = splitLocale === "fr-CA";

  //onClick function if category is link
  const handleCategoryLinkClick = useCallback(
    (event: React.MouseEvent, category: NavigationElement) => {
      // Dispatch analytics event
      const target = event?.target as HTMLElement;
      const { id, className } = target;
      document?.body?.dispatchEvent(
        new CustomEvent("cta_click", {
          detail: {
            item: {
              isHeaderNavigationNew: true,
              topCat: category?.displayName,
              parentCat: category?.targetUrl,
              cat: category?.targetUrl,
              catURL: category?.targetUrl,
              class: className,
              id: id
            }
          }
        })
      );

      if (typeof window === "undefined") return;

      if (
        category?.linkType === "none" ||
        category?.targetUrl?.startsWith("https://")
      ) {
        window.open(category?.targetUrl ?? "", "_blank");
      } else {
        window.location.assign(buildPath(`${locale}${category?.targetUrl}`));
      }
    },
    [locale]
  );

  //onClick function if category is dropdown
  const handleCategoryDropdownToggle = useCallback(
    (categoryIndex: number) => {
      if (categoryIndex === headerState.selectedCategoryIndex) {
        closeCategoryDropdown();
      } else {
        openCategoryDropdown(categoryIndex);
      }
    },
    [
      headerState.selectedCategoryIndex,
      closeCategoryDropdown,
      openCategoryDropdown
    ]
  );

  return (
    <CatalogNavigation
      categories={categories}
      selectedCategoryIndex={headerState.selectedCategoryIndex}
      isFrenchCanada={isFrenchCanada}
      onCategoryLinkClick={handleCategoryLinkClick}
      onCategoryDropdownToggle={handleCategoryDropdownToggle}
    />
  );
};

export const MemoizedCatalogNavigationContainer = memoize(
  CatalogNavigationContainer
);
